import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardAccount, CardAccountRepository } from '~/repositories/CardAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';

import { CardAccountProps } from './CardAccount';

interface Params {
  id: string;
}

const cardAccountQueryKey = (id: string) => ['card-account', id] as const;

const ActionCard = styled.div<{ $type?: 'success' | 'warning' | 'default' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme, $type }) =>
    $type === 'success'
      ? theme.success.blendToBackground(50)
      : $type === 'warning'
        ? theme.warning.blendToBackground(50)
        : theme.secondary.blendToBackground(50)};
  border: 1px solid
    ${({ theme, $type }) =>
      $type === 'success'
        ? theme.success.blendToBackground(150)
        : $type === 'warning'
          ? theme.warning.blendToBackground(150)
          : theme.secondary.blendToBackground(100)};
`;

const ActionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionIconWrapper = styled.div<{ $type?: 'success' | 'warning' | 'default' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme, $type }) =>
    $type === 'success'
      ? theme.success.blendToBackground(100)
      : $type === 'warning'
        ? theme.warning.blendToBackground(100)
        : theme.secondary.blendToBackground(100)};

  svg {
    --icon-size: 20px;
    --icon-color: ${({ theme, $type }) =>
      $type === 'success'
        ? theme.success.toString()
        : $type === 'warning'
          ? theme.warning.toString()
          : theme.secondary.blendToBackground(700)};
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
`;

const ActionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoActionsText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  padding: 8px 0;
`;

export const PageCardAccountActions: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<keyof Params>() as Params;
  const { cardAccount: outletCardAccount } = useOutletContext<CardAccountProps>();

  const { data: cardAccount, error: cardAccountError } = useQuery({
    queryKey: cardAccountQueryKey(id!),
    queryFn: () => CardAccountRepository.get(id!),
    enabled: !!id && !outletCardAccount,
    initialData: outletCardAccount,
  });

  const suspendMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card account ID is required');
      return CardAccountRepository.suspend(id);
    },
    onSuccess: (response: CardAccount) => {
      queryClient.setQueryData(cardAccountQueryKey(id!), response);
      addSuccessNotification({
        content: 'Card account suspended successfully',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error?.message || 'Failed to suspend card account',
      });
    },
  });

  const reinstateMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card account ID is required');
      return CardAccountRepository.reinstate(id);
    },
    onSuccess: (response: CardAccount) => {
      queryClient.setQueryData(cardAccountQueryKey(id!), response);
      addSuccessNotification({
        content: 'Card account reinstated successfully',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error?.message || 'Failed to reinstate card account',
      });
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card account ID is required');
      return CardAccountRepository.close(id);
    },
    onSuccess: (response: CardAccount) => {
      queryClient.setQueryData(cardAccountQueryKey(id!), response);
      addSuccessNotification({
        content: 'Card account closed successfully',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error?.message || 'Failed to close card account',
      });
    },
  });

  useEffect(() => {
    if (cardAccountError) {
      navigate(ROUTE.CARD_ACCOUNTS);
      addDangerNotification({
        content: (cardAccountError as any).message || 'Failed to load card account',
      });
    }
  }, [cardAccountError, navigate, addDangerNotification]);

  if (!cardAccount) {
    return null;
  }

  const status = (cardAccount.status || '').toLowerCase();
  const canSuspend = status === 'open';
  const canReinstate = status === 'suspended' || status === 'closed';
  const canClose = status !== 'closed';
  const isActionPending = suspendMutation.isPending || reinstateMutation.isPending || closeMutation.isPending;

  return (
    <Inner p={0}>
      <Section headline="Card Account Status">
        <Inner px="var(--page-margin)" py={16}>
          <ActionsGrid>
            {canReinstate && (
              <ActionCard $type="warning">
                <ActionInfo>
                  <ActionIconWrapper $type="warning">
                    <Icon.Offline />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Card Account is Inactive</ActionTitle>
                    <ActionDescription>This account is currently not active for card activity.</ActionDescription>
                  </ActionText>
                </ActionInfo>
                <Button
                  variant="primary"
                  icon={<Icon.Play />}
                  onClick={() => reinstateMutation.mutate()}
                  isLoading={reinstateMutation.isPending}
                  isDisabled={isActionPending}
                >
                  Reinstate Card Account
                </Button>
              </ActionCard>
            )}

            {canSuspend && (
              <ActionCard $type="success">
                <ActionInfo>
                  <ActionIconWrapper $type="success">
                    <Icon.CircleCheck />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Card Account is Open</ActionTitle>
                    <ActionDescription>This account is active and can issue or use cards.</ActionDescription>
                  </ActionText>
                </ActionInfo>
                <Button
                  variant="secondary"
                  icon={<Icon.Offline />}
                  onClick={() => suspendMutation.mutate()}
                  isLoading={suspendMutation.isPending}
                  isDisabled={isActionPending}
                >
                  Suspend Card Account
                </Button>
              </ActionCard>
            )}

            {canClose && (
              <ActionCard>
                <ActionInfo>
                  <ActionIconWrapper>
                    <Icon.Close />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Close Card Account</ActionTitle>
                    <ActionDescription>
                      This action permanently closes the account for future card activity.
                    </ActionDescription>
                  </ActionText>
                </ActionInfo>
                <Button
                  variant="secondary"
                  icon={<Icon.Close />}
                  onClick={() => closeMutation.mutate()}
                  isLoading={closeMutation.isPending}
                  isDisabled={isActionPending}
                >
                  Close Card Account
                </Button>
              </ActionCard>
            )}

            {!canSuspend && !canReinstate && !canClose && (
              <NoActionsText>No actions available for this card account status.</NoActionsText>
            )}
          </ActionsGrid>
        </Inner>
      </Section>
    </Inner>
  );
};
